import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DisplayTypeDto } from "clearline-api";
import { ALL_DEVICE_TYPE_ID, DeviceTypeIconComponent } from "./device-type-icon/device-type-icon.component";

export type DeviceTypeSelectorMode = "single" | "multi";

/**
 * Visual device-type ("Screen Orientation") selector.
 *
 * Renders each option as a selectable card (device icon + label + selected state)
 * and works as a reactive-form control via {@link ControlValueAccessor}:
 * - `single` mode behaves like a radio group and emits a single `number` id.
 * - `multi` mode behaves like a checkbox group and emits a `number[]` of ids.
 *
 * In `multi` mode an optional "All" card can be enabled via `withAllOption`. "All" is a
 * separate two-way `allSelected` flag (kept in sync with e.g. `useAllDisplayTypes`) — the
 * control value stays the `number[]` of individual ids and is empty while "All" is active,
 * preserving the existing API contract.
 */
@Component({
  selector: "app-device-type-selector",
  templateUrl: "./device-type-selector.component.html",
  styleUrls: ["./device-type-selector.component.scss"],
  standalone: true,
  imports: [DeviceTypeIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DeviceTypeSelectorComponent),
      multi: true
    }
  ]
})
export class DeviceTypeSelectorComponent implements ControlValueAccessor {
  readonly items = input<DisplayTypeDto[]>([]);
  readonly mode = input<DeviceTypeSelectorMode>("single");
  readonly withAllOption = input<boolean>(false);
  readonly allLabel = input<string>("All");

  /** Two-way "All" flag; mirrors the domain's `useAllDisplayTypes`. */
  readonly allSelected = model<boolean>(false);

  readonly disabled = signal(false);
  readonly isMulti = computed<boolean>(() => this.mode() === "multi");
  readonly showAllOption = computed<boolean>(() => this.isMulti() && this.withAllOption());

  protected readonly allTypeId = ALL_DEVICE_TYPE_ID;

  private readonly singleValue = signal<number | null>(null);
  private readonly multiValue = signal<number[]>([]);
  private readonly allIds = computed<number[]>(() => this.items().map((item: DisplayTypeDto) => item.id));

  private onChange: (value: number | number[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  isSelected(item: DisplayTypeDto): boolean {
    if (this.isMulti()) {
      // While "All" is active every individual card reads as selected (AC11).
      return this.allSelected() || this.multiValue().includes(item.id);
    }

    return this.singleValue() === item.id;
  }

  /** Toggle the synthetic "All" card. */
  selectAll(): void {
    if (this.disabled()) {
      return;
    }

    this.onTouched();
    // "All" => empty individual set, flag drives the selection (preserves API contract).
    this.setMultiSelection([], !this.allSelected());
  }

  selectItem(item: DisplayTypeDto): void {
    if (this.disabled()) {
      return;
    }

    this.onTouched();

    if (!this.isMulti()) {
      if (this.singleValue() !== item.id) {
        this.singleValue.set(item.id);
        this.onChange(item.id);
      }
      return;
    }

    if (this.allSelected()) {
      // Un-checking one card while "All" is active expands to "all except this one" (AC11).
      this.setMultiSelection(this.allIds().filter((id: number) => id !== item.id), false);
      return;
    }

    const next = this.multiValue().includes(item.id)
      ? this.multiValue().filter((id: number) => id !== item.id)
      : [...this.multiValue(), item.id];

    // Selecting every individual option collapses back to "All" (AC11).
    if (this.showAllOption() && next.length && next.length === this.allIds().length) {
      this.setMultiSelection([], true);
    } else {
      this.setMultiSelection(next, false);
    }
  }

  // ControlValueAccessor

  writeValue(value: number | number[] | null): void {
    if (this.isMulti()) {
      this.multiValue.set(Array.isArray(value) ? [...value] : []);
    } else {
      this.singleValue.set(typeof value === "number" ? value : null);
    }
  }

  registerOnChange(fn: (value: number | number[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private setMultiSelection(ids: number[], all: boolean): void {
    // Update the "All" flag first so the control validator sees the final state.
    if (this.allSelected() !== all) {
      this.allSelected.set(all);
    }

    this.multiValue.set(ids);
    this.onChange([...ids]);
  }
}
